// MultiCdmGradeView.js
// -----------------------------------------------------------------------------
// Forked from NewGrading/NewGrading.js.
//
// Why a fork and not a reuse: NewGrading fetches its own grade on mount, by
// caseId, via /viewcasegrade.webapi -> newest CustomerGradeId. Inside a block we
// need THIS attempt's grade, not the newest one — a case redone outside the
// block would otherwise show the wrong grade. No prop can redirect that, so the
// fetch has to go, and once it goes most of the component's plumbing goes too.
//
// CDM's grading is shaped completely differently from CCS's, so this is NOT a
// straight rename of MultiGradeView — the feedback section was rewritten:
//   - CCS grades a flat list of order events, grouped into six categories
//     (Diagnosis/Treatment/Preventive/Timing/Appropriate/Sequence).
//   - CDM grades per QUESTION: grade.pgolist.pgolist is one entry per
//     question, each with its own grade %, the customer's answer, and its own
//     GradeOut list of graded criteria (colored via GradeColor, not a
//     precomputed Correct flag or an "Optional Order" string match).
//   - CDM has no order count, so there's no Average Orders chart.
//   - CDM has no sequential action log (the per-question breakdown already
//     covers "what happened"), so there's no Action Log section either.
//   - CDM has no per-item Importance rating, so there's no importance bar.
//   - Case Summary lives at grade.CaseSummary directly, not grade.GradeFeedback.SUMMARY.
//   - Case Diagnosis comes from the case row (caseRow.DIAGNOSIS), the same
//     place NewGrading.js's own selectedCaseDiagnosis prop gets it from — not
//     from the grade envelope.
//
// What's kept: score cards, the case summary, the highlighter, reading
// options, high yield, and review later.
//
// What's dropped: "Try Case Again" and "View Previous Completions" (both fight
// the idea of a finished block), the navbar (the review sidebar navigates), and
// the self-fetch (MultiCdmReview passes the grade in).
//
// It also does NOT touch document-level listeners or <meta name=theme-color>
// the way NewGrading does: this renders inside the MultiCDM overlay, and an
// unmount here must leave the host app exactly as it found it. Copy protection
// is scoped to this subtree instead.
//
// Reuses NewGrading.css — same classes, same look as the main grading page.
//
// Props:
//   grade            grade envelope from /retreivemulticdmcasegrade.webapi (required)
//   caseRow          merged case row for the title block               (required)
//   api              MultiCDM api layer                                (required)
//   userData         login payload (TimeZone for date formatting)      (required)
//   readingOptions   saved reading options, or null                    (optional)
//   onReadingOptions (next) => void — persist                          (optional)
//   reviewLater      bool                                              (required)
//   onReviewLater    (caseId, marked) => void                          (required)
//   primaryColor     string                                            (optional)
//   selectedTheme    theme object                                      (optional)
//   themeNumber      raw theme index — same value App.js feeds NewGrading's
//                    own themeNumber prop, used only to dim feedback cards on
//                    non-default themes                                (optional)
//   animationsDisabled bool                                            (optional)
// -----------------------------------------------------------------------------

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faQuestionCircle,
  faHighlighter,
  faEraser,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faUserDoctorMessage } from "@fortawesome/pro-solid-svg-icons";
import MDSpinner from "react-md-spinner";
import { AnimatePresence, motion } from "framer-motion/dist/framer-motion";
import formatDateString from "../Functions/FormatDateString";
import formatTimeString from "../Functions/FormatTimeString";
import "../NewGrading/NewGrading.css";

const DEFAULT_READING_OPTIONS = {
  readingFontSize: "medium",
  readingFontFamily: "sans",
  readingLineHeight: "normal",
  readingLetterSpacing: "normal",
  readingColorScheme: "default",
};

class MultiCdmGradeView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      highYield: !!(props.grade && props.grade.HighYieldFlag),
      highYieldLoading: false,
      reviewLaterLoading: false,
      showReadingOptions: false,
      readingOptions: Object.assign(
        {},
        DEFAULT_READING_OPTIONS,
        props.readingOptions || {},
      ),
      highlighterActive: false,
      highlights: [],
      // --- Feedback button/modal ---
      showFeedbackModal: false,
      feedbackText: "",
      feedbackLoading: false,
      feedbackSubmitted: false,
      feedbackError: null,
    };

    this.pageRef = React.createRef();
    // Registry key is per-case so switching cases in the sidebar can't leave a
    // previous case's ranges registered.
    this.HIGHLIGHT_REGISTRY_KEY =
      "multiGradingHighlights-" + (props.caseRow ? props.caseRow.ID : "x");
    this.highlightApiSupported =
      typeof window !== "undefined" &&
      typeof window.Highlight === "function" &&
      typeof CSS !== "undefined" &&
      !!CSS.highlights;
  }

  componentDidMount() {
    document.addEventListener("selectionchange", this.handleSelectionChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.readingOptions !== this.props.readingOptions) {
      this.setState({
        readingOptions: Object.assign(
          {},
          DEFAULT_READING_OPTIONS,
          this.props.readingOptions || {},
        ),
      });
    }

    if (prevProps.grade !== this.props.grade) {
      // Case switched — any pending debounced selection from the previous
      // case is now stale (it would resolve against the wrong container
      // set / registry key), so drop it.
      if (this.selectionSettleTimer) {
        clearTimeout(this.selectionSettleTimer);
        this.selectionSettleTimer = null;
      }
      this.setState({
        highYield: !!(this.props.grade && this.props.grade.HighYieldFlag),
        highlights: [],
      });
    }

    if (
      prevState.highlights !== this.state.highlights ||
      prevProps.grade !== this.props.grade ||
      prevState.readingOptions !== this.state.readingOptions
    ) {
      this.applyHighlightsToDom();
    }
  }

  componentWillUnmount() {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    }

    // --- Highlighter (mobile) ---
    document.removeEventListener("selectionchange", this.handleSelectionChange);
    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }
  }

  // --- copy protection, scoped to this subtree -------------------------------
  // NewGrading binds these on `document`; here they're React handlers on the
  // wrapper so nothing leaks out of the overlay.

  blockClipboard = (e) => {
    e.preventDefault();
  };

  blockContextMenu = (e) => {
    e.preventDefault();
  };

  blockKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "x")) {
      e.preventDefault();
    }
  };

  // --- review later / high yield --------------------------------------------

  handleReviewLater = () => {
    const caseId = this.props.caseRow.ID;
    const next = !this.props.reviewLater;
    this.setState({ reviewLaterLoading: true });
    this.props.api
      .saveReviewLater(caseId, next)
      .then(() => {
        this.setState({ reviewLaterLoading: false });
        this.props.onReviewLater(caseId, next);
      })
      .catch(() => {
        this.setState({ reviewLaterLoading: false });
      });
  };

  handleHighYield = () => {
    const caseId = this.props.caseRow.ID;
    const next = !this.state.highYield;
    // Optimistic, reverted on failure — same as NewGrading.
    this.setState({ highYield: next, highYieldLoading: true });
    this.props.api
      .saveHighYield(caseId, next)
      .then(() => {
        this.setState({ highYieldLoading: false });
      })
      .catch(() => {
        this.setState({ highYield: !next, highYieldLoading: false });
      });
  };

  // --- feedback ---------------------------------------------------------------
  // Same flow as NewGrading's feedback modal, routed through the api layer
  // (like saveHighYield/saveReviewLater above) instead of fetching directly —
  // this component doesn't own route/Token/auth-error handling, the api layer
  // does.

  submitFeedback = () => {
    if (!this.state.feedbackText.trim()) {
      return;
    }

    this.setState({ feedbackLoading: true, feedbackError: null });

    const caseId = this.props.caseRow ? this.props.caseRow.ID : null;
    const caseDiagnosis = this.props.caseRow
      ? this.props.caseRow.DIAGNOSIS
      : null;

    this.props.api
      .submitFeedback({
        feedback: this.state.feedbackText,
        caseId: caseId,
        caseDiagnosis: caseDiagnosis,
      })
      .then(() => {
        this.setState({
          feedbackLoading: false,
          feedbackSubmitted: true,
          feedbackText: "",
        });
        setTimeout(() => {
          this.setState({
            showFeedbackModal: false,
            feedbackSubmitted: false,
          });
        }, 2000);
      })
      .catch(() => {
        this.setState({
          feedbackLoading: false,
          feedbackError: "Failed to submit feedback. Please try again.",
        });
      });
  };

  closeFeedbackModal = () => {
    this.setState({
      showFeedbackModal: false,
      feedbackText: "",
      feedbackSubmitted: false,
      feedbackError: null,
    });
  };

  // --- reading options -------------------------------------------------------

  patchReadingOptions = (changes) => {
    const next = Object.assign({}, this.state.readingOptions, changes);
    this.setState({ readingOptions: next });
    if (this.props.onReadingOptions) this.props.onReadingOptions(next);
  };

  resetReadingOptions = () => {
    const next = Object.assign({}, DEFAULT_READING_OPTIONS);
    this.setState({ readingOptions: next });
    if (this.props.onReadingOptions) this.props.onReadingOptions(next);
  };

  getReadingStyle = (verdict) => {
    const options = this.state.readingOptions;
    const fontSizeMap = {
      small: "13px",
      medium: "15px",
      large: "17px",
      xlarge: "20px",
    };
    const fontFamilyMap = {
      sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      serif: "Georgia, 'Times New Roman', serif",
      mono: "'Courier New', Courier, monospace",
    };
    const lineHeightMap = { compact: "1.4", normal: "1.7", relaxed: "2.1" };
    const letterSpacingMap = { normal: "0px", wide: "0.04em" };

    const colorSchemes = {
      default: {
        neutral: { color: "var(--new-text-color)" },
      },
      mono: {
        correct: { backgroundColor: "#f0f0f0", color: "#1a1a1a" },
        incorrect: { backgroundColor: "#8a8a8a", color: "#f5f5f5" },
        optional: { backgroundColor: "#cbcbcb", color: "#1a1a1a" },
        neutral: { backgroundColor: "#e0e0e0", color: "#1a1a1a" },
      },
      sepia: {
        correct: { backgroundColor: "#f0e8cf", color: "#3b2f14" },
        incorrect: { backgroundColor: "#b89a70", color: "#2a1f0a" },
        optional: { backgroundColor: "#ddd0b0", color: "#3b2f14" },
        neutral: { backgroundColor: "#ede5cc", color: "#3b2f14" },
      },
      cool: {
        correct: { backgroundColor: "#e8f4fc", color: "#0d2a3a" },
        incorrect: { backgroundColor: "#7aafd4", color: "#0d1e2e" },
        optional: { backgroundColor: "#c0daf0", color: "#0d2a3a" },
        neutral: { backgroundColor: "#daeaf7", color: "#0d2a3a" },
      },
      highcontrast: {
        correct: { backgroundColor: "#003300", color: "#ccffcc" },
        incorrect: { backgroundColor: "#330000", color: "#ffcccc" },
        optional: { backgroundColor: "#1a1a00", color: "#ffffcc" },
        neutral: { backgroundColor: "#00134d", color: "#cce0ff" },
      },
    };

    const scheme = colorSchemes[options.readingColorScheme];
    const colorOverride = (scheme && scheme[verdict || "neutral"]) || {};

    return Object.assign(
      {
        fontSize: fontSizeMap[options.readingFontSize],
        fontFamily: fontFamilyMap[options.readingFontFamily],
        lineHeight: lineHeightMap[options.readingLineHeight],
        letterSpacing: letterSpacingMap[options.readingLetterSpacing],
      },
      colorOverride,
    );
  };

  // --- highlighter -----------------------------------------------------------
  // Same approach as NewGrading: store character offsets within a labelled
  // container, re-anchor by text if the offsets drift, and paint with the CSS
  // Custom Highlight API where available, <mark> wrapping where not.

  toggleHighlighter = () => {
    this.setState(function (prevState) {
      return { highlighterActive: !prevState.highlighterActive };
    });
  };

  clearAllHighlights = () => {
    this.setState({ highlights: [] });
  };

  findHighlightContainer = (containerKey) => {
    const root = this.pageRef.current || document;
    const containers = root.querySelectorAll(
      "[data-highlight-container='true']",
    );
    for (let i = 0; i < containers.length; i++) {
      if (containers[i].dataset.highlightKey === containerKey) {
        return containers[i];
      }
    }
    return null;
  };

  getTextNodesIn = (container) => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      nodes.push(node);
    }
    return nodes;
  };

  getOffsetWithin = (container, node, offset) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(container);
      range.setEnd(node, offset);
      return range.toString().length;
    } catch (error) {
      return null;
    }
  };

  createRangeFromOffsets = (container, start, end) => {
    const textNodes = this.getTextNodesIn(container);
    let charCount = 0;
    let startNode = null;
    let startOffset = 0;
    let endNode = null;
    let endOffset = 0;

    for (let i = 0; i < textNodes.length; i++) {
      const node = textNodes[i];
      const length = node.textContent.length;
      if (startNode === null && start <= charCount + length) {
        startNode = node;
        startOffset = start - charCount;
      }
      if (startNode !== null && end <= charCount + length) {
        endNode = node;
        endOffset = end - charCount;
        break;
      }
      charCount += length;
    }

    if (!startNode || !endNode) return null;

    try {
      const range = document.createRange();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      return range;
    } catch (error) {
      return null;
    }
  };

  applyHighlightsToDom = () => {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    } else {
      this.clearHighlightMarks();
    }

    if (this.state.highlights.length === 0) return;

    const resolvedRanges = [];
    this.state.highlights.forEach((highlight) => {
      const container = this.findHighlightContainer(highlight.containerKey);
      if (!container) return;

      let range = this.createRangeFromOffsets(
        container,
        highlight.startOffset,
        highlight.endOffset,
      );

      // Offsets can drift if the text re-renders; fall back to finding the
      // stored text.
      if (!range || range.toString() !== highlight.text) {
        const fullText = container.textContent;
        const index = fullText.indexOf(highlight.text);
        if (index === -1) return;
        range = this.createRangeFromOffsets(
          container,
          index,
          index + highlight.text.length,
        );
        if (!range) return;
      }

      resolvedRanges.push({ range: range, id: highlight.id });
    });

    if (resolvedRanges.length === 0) return;

    if (this.highlightApiSupported) {
      CSS.highlights.set(
        this.HIGHLIGHT_REGISTRY_KEY,
        new window.Highlight(
          ...resolvedRanges.map(function (entry) {
            return entry.range;
          }),
        ),
      );
    } else {
      resolvedRanges
        .slice()
        .reverse()
        .forEach((entry) => this.wrapRangeInMarks(entry.range, entry.id));
    }
  };

  wrapRangeInMarks = (range, highlightId) => {
    const root =
      range.commonAncestorContainer.nodeType === Node.TEXT_NODE
        ? range.commonAncestorContainer.parentNode
        : range.commonAncestorContainer;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const intersecting = [];
    let node;
    while ((node = walker.nextNode())) {
      if (range.intersectsNode(node)) intersecting.push(node);
    }

    intersecting.reverse().forEach((textNode) => {
      const start = textNode === range.startContainer ? range.startOffset : 0;
      const end =
        textNode === range.endContainer
          ? range.endOffset
          : textNode.textContent.length;
      if (end <= start) return;

      let nodeToWrap = textNode;
      if (start > 0) nodeToWrap = textNode.splitText(start);
      if (end - start < nodeToWrap.textContent.length) {
        nodeToWrap.splitText(end - start);
      }

      const mark = document.createElement("mark");
      mark.className = "newGradingTextHighlight";
      mark.dataset.highlightId = highlightId;
      nodeToWrap.parentNode.insertBefore(mark, nodeToWrap);
      mark.appendChild(nodeToWrap);
    });
  };

  clearHighlightMarks = () => {
    const root = this.pageRef.current || document;
    root.querySelectorAll("mark.newGradingTextHighlight").forEach((mark) => {
      const parent = mark.parentNode;
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
      parent.removeChild(mark);
      parent.normalize();
    });
  };

  // Extracts { clientX, clientY } from either a MouseEvent or a TouchEvent
  // (touchend events carry their coordinates on changedTouches, not on the
  // event itself).
  getEventPoint = (event) => {
    if (event.changedTouches && event.changedTouches.length > 0) {
      return {
        clientX: event.changedTouches[0].clientX,
        clientY: event.changedTouches[0].clientY,
      };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  };

  // Turns the current, non-collapsed window selection into a stored
  // highlight (if it falls inside a highlight-able container). Shared by the
  // desktop mouseup path and the mobile selectionchange path.
  commitSelectionAsHighlight = (selection) => {
    const rawRange = selection.getRangeAt(0);
    const startElement =
      rawRange.startContainer.nodeType === Node.TEXT_NODE
        ? rawRange.startContainer.parentElement
        : rawRange.startContainer;
    const container =
      startElement &&
      startElement.closest &&
      startElement.closest("[data-highlight-container='true']");

    if (!container) {
      selection.removeAllRanges();
      return;
    }

    const range = rawRange.cloneRange();
    if (!container.contains(range.endContainer)) {
      range.setEnd(container, container.childNodes.length);
    }

    const startOffset = this.getOffsetWithin(
      container,
      range.startContainer,
      range.startOffset,
    );
    const endOffset = this.getOffsetWithin(
      container,
      range.endContainer,
      range.endOffset,
    );

    if (
      startOffset === null ||
      endOffset === null ||
      endOffset <= startOffset
    ) {
      selection.removeAllRanges();
      return;
    }

    const text = range.toString();
    if (!text.trim()) {
      selection.removeAllRanges();
      return;
    }

    const highlight = {
      id: "hl-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      containerType: container.dataset.highlightType,
      containerKey: container.dataset.highlightKey,
      startOffset: startOffset,
      endOffset: endOffset,
      text: text,
      createdAt: new Date().toISOString(),
    };

    selection.removeAllRanges();
    this.setState(function (prevState) {
      return { highlights: prevState.highlights.concat([highlight]) };
    });
  };

  handleHighlightMouseUp = (event) => {
    if (!this.state.highlighterActive) return;

    const selection = window.getSelection();
    if (!selection) return;

    if (selection.isCollapsed || selection.rangeCount === 0) {
      this.removeHighlightAtPoint(event);
      return;
    }

    this.commitSelectionAsHighlight(selection);
  };

  handleSelectionChange = () => {
    if (!this.state.highlighterActive) return;

    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }

    this.selectionSettleTimer = setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return;
      }
      this.commitSelectionAsHighlight(selection);
    }, 300);
  };

  handleHighlightTouchEnd = (event) => {
    if (!this.state.highlighterActive) return;

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      return;
    }

    this.removeHighlightAtPoint(event);
  };

  removeHighlightAtPoint = (event) => {
    const markElement =
      event.target &&
      event.target.closest &&
      event.target.closest("mark.newGradingTextHighlight");
    if (markElement) {
      const id = markElement.dataset.highlightId;
      this.setState(function (prevState) {
        return {
          highlights: prevState.highlights.filter(function (h) {
            return h.id !== id;
          }),
        };
      });
      return;
    }

    const container =
      event.target &&
      event.target.closest &&
      event.target.closest("[data-highlight-container='true']");
    if (!container) return;

    const point = this.getEventPoint(event);
    const clientX = point.clientX;
    const clientY = point.clientY;

    let node = null;
    let offset = 0;
    if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(clientX, clientY);
      if (position) {
        node = position.offsetNode;
        offset = position.offset;
      }
    } else if (document.caretRangeFromPoint) {
      const caretRange = document.caretRangeFromPoint(clientX, clientY);
      if (caretRange) {
        node = caretRange.startContainer;
        offset = caretRange.startOffset;
      }
    }

    if (!node || !container.contains(node)) return;

    const globalOffset = this.getOffsetWithin(container, node, offset);
    if (globalOffset === null) return;

    const containerKey = container.dataset.highlightKey;
    const match = this.state.highlights.find(function (h) {
      return (
        h.containerKey === containerKey &&
        globalOffset >= h.startOffset &&
        globalOffset <= h.endOffset
      );
    });
    if (match) {
      this.setState(function (prevState) {
        return {
          highlights: prevState.highlights.filter(function (h) {
            return h.id !== match.id;
          }),
        };
      });
    }
  };

  // Mirrors NewGrading.getMultipleChoiceOption, reading from the grade
  // envelope's own ao list instead of component state.
  getMultipleChoiceOption(answerId) {
    const ao = this.props.grade.ao || [];
    const match = ao.find(function (answerValue) {
      return answerValue.ANSWERID === answerId;
    });
    return match ? match.ANSWER : "";
  }

  // --- render ---------------------------------------------------------------

  renderReadingOptions() {
    const options = this.state.readingOptions;

    return (
      <div className="readingOptionsWrapper">
        <button
          type="button"
          className="newGradingViewPreviousButton tertiaryButton readingOptionsToggle"
          onClick={() => {
            this.setState(function (prevState) {
              return { showReadingOptions: !prevState.showReadingOptions };
            });
          }}
        >
          <span className="readingOptionsIcon">Aa</span>
          <span className="readingOptionsText">Reading Options</span>
        </button>
        {this.state.showReadingOptions && (
          <>
            <div
              className="readingOptionsBackdrop"
              onClick={() => {
                this.setState({ showReadingOptions: false });
              }}
            />
            <div
              className="readingOptionsPanel"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Font Size</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "small", label: "S" },
                    { key: "medium", label: "M" },
                    { key: "large", label: "L" },
                    { key: "xlarge", label: "XL" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingFontSize === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingFontSize: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Typeface</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "sans", label: "Sans" },
                    { key: "serif", label: "Serif" },
                    { key: "mono", label: "Mono" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingFontFamily === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingFontFamily: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Line Spacing</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "compact", label: "Compact" },
                    { key: "normal", label: "Normal" },
                    { key: "relaxed", label: "Relaxed" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingLineHeight === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingLineHeight: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Letter Spacing</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "normal", label: "Normal" },
                    { key: "wide", label: "Wide" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingLetterSpacing === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({
                          readingLetterSpacing: key,
                        });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Color Scheme</span>
                <div className="readingOptionsColorRow">
                  {[
                    {
                      key: "default",
                      label: "Default",
                      swatches: ["#e2ffdd", "#ffdddd", "#d1ecf1"],
                    },
                    {
                      key: "mono",
                      label: "Mono",
                      swatches: ["#f0f0f0", "#8a8a8a", "#cbcbcb"],
                    },
                    {
                      key: "sepia",
                      label: "Sepia",
                      swatches: ["#f0e8cf", "#b89a70", "#ddd0b0"],
                    },
                    {
                      key: "cool",
                      label: "Cool",
                      swatches: ["#e8f4fc", "#7aafd4", "#c0daf0"],
                    },
                    {
                      key: "highcontrast",
                      label: "High Contrast",
                      swatches: ["#003300", "#330000", "#00134d"],
                    },
                  ].map(({ key, label, swatches }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingColorChip" +
                        (options.readingColorScheme === key
                          ? " readingColorChipActive"
                          : "")
                      }
                      title={label}
                      onClick={() => {
                        this.patchReadingOptions({ readingColorScheme: key });
                      }}
                    >
                      <span className="readingColorSwatches">
                        {swatches.map(function (bg, i) {
                          return (
                            <span
                              key={i}
                              className="readingColorSwatch"
                              style={{ backgroundColor: bg }}
                            />
                          );
                        })}
                      </span>
                      <span className="readingColorLabel">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="readingOptionsReset"
                onClick={this.resetReadingOptions}
              >
                Reset to defaults
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  render() {
    const { grade, caseRow, userData, primaryColor, reviewLater } = this.props;

    if (!grade || !grade.pgolist || !Array.isArray(grade.pgolist.pgolist)) {
      return (
        <div className="newGradingPageWrapper">
          <div className="newGradingLoaderContainer">
            <MDSpinner size={60} singleColor={primaryColor} />
            <p className="newGradingLoaderText">Loading grades</p>
          </div>
        </div>
      );
    }

    return (
      <div
        className={
          "newGradingPageWrapper" +
          (this.state.highlighterActive ? " newGradingHighlighterActive" : "")
        }
        ref={this.pageRef}
        onMouseUp={this.handleHighlightMouseUp}
        onTouchEnd={this.handleHighlightTouchEnd}
        onCopy={this.blockClipboard}
        onCut={this.blockClipboard}
        onContextMenu={this.blockContextMenu}
        onKeyDown={this.blockKeyDown}
      >
        <style>{`
          ::highlight(${this.HIGHLIGHT_REGISTRY_KEY}) {
            background-color: rgba(255, 214, 10, 0.55);
          }
          mark.newGradingTextHighlight {
            background-color: rgba(255, 214, 10, 0.55);
            color: inherit;
            padding: 0;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] {
            cursor: text;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] ::selection {
            background-color: rgba(255, 214, 10, 0.45);
          }
        `}</style>

        {/* Title block */}
        <div className="newGradingCaseTitleContainer">
          <div className="newGradingCaseNumberContainer">
            <h1 className="newGradingCaseNumber">{caseRow.ID}</h1>
          </div>
          <div className="newGradingCaseTitleWrapper">
            <h2 className="newGradingCaseTitle">
              {caseRow.TITLE} - {caseRow.CASECATEGORYDESC}
            </h2>
            <h3 className="newGradingCaseCompletionDate">
              Completed:{" "}
              {formatDateString(grade.CaseCompletionDate, userData.TimeZone)},{" "}
              {formatTimeString(grade.CaseCompletionDate, userData.TimeZone)}
            </h3>
          </div>
          <div className="grading-button-block">
            <div className="highlightingContainer">
              <button
                type="button"
                className="tertiaryButton newGradingBackButton"
                onClick={this.toggleHighlighter}
                title={
                  this.state.highlighterActive
                    ? "Highlighter on \u2014 select text to highlight it, click a highlight to remove it"
                    : "Turn on the highlighter"
                }
                style={
                  this.state.highlighterActive
                    ? { boxShadow: "0 0 0 2px rgba(255, 214, 10, 0.9) inset" }
                    : {}
                }
              >
                <FontAwesomeIcon
                  icon={faHighlighter}
                  className="newGradingBackIcon"
                />
                {this.state.highlighterActive
                  ? "Highlighter On"
                  : "Highlighter"}
                {this.state.highlights.length > 0 &&
                  " (" + this.state.highlights.length + ")"}
              </button>
              {this.state.highlights.length > 0 && (
                <button
                  type="button"
                  className="tertiaryButton newGradingBackButton"
                  onClick={this.clearAllHighlights}
                  title="Remove all highlights"
                >
                  <FontAwesomeIcon
                    icon={faEraser}
                    className="newGradingBackIcon"
                  />
                  Clear Highlights
                </button>
              )}
            </div>

            <button
              type="button"
              className="newGradingViewPreviousButton tertiaryButton"
              onClick={() => this.setState({ showFeedbackModal: true })}
            >
              Feedback
            </button>

            <div
              className="newGradingCheckboxContainer"
              onClick={this.handleHighYield}
              style={{
                gridTemplateColumns: "30px auto auto",
                paddingRight: "10px",
              }}
            >
              <div className="newGradingCheckbox">
                {this.state.highYield && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="newGradingCheckboxIcon"
                  />
                )}
              </div>
              <p className="newGradingCheckboxText">High Yield</p>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="highYieldMoreInfoButton"
                title="Mark this case if you think it has a good chance of showing up on the exam."
              />
              {this.state.highYieldLoading && (
                <div className="highYieldLoadingContainer">
                  <MDSpinner size={35} singleColor={"#fff"} />
                </div>
              )}
            </div>

            <div
              className="newGradingCheckboxContainer"
              onClick={this.handleReviewLater}
              style={{ gridTemplateColumns: "30px auto" }}
            >
              <div className="newGradingCheckbox">
                {reviewLater && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="newGradingCheckboxIcon"
                  />
                )}
              </div>
              <p className="newGradingCheckboxText">Review Later</p>
              {this.state.reviewLaterLoading && (
                <div className="highYieldLoadingContainer">
                  <MDSpinner size={30} singleColor={"#fff"} />
                </div>
              )}
            </div>

            {this.renderReadingOptions()}
          </div>
        </div>

        {/* Score cards */}
        <div
          className="newGradingCaseDetailsWrapper"
          data-highlight-container="true"
          data-highlight-key="caseDetails"
          data-highlight-type="caseDetails"
        >
          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUser}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">Your Score</h3>
            <h2 className="newGradingCaseDetailValue">
              {grade.GradeForCase
                ? Math.round(grade.GradeForCase * 100) / 100
                : 0}
              %
            </h2>
          </div>

          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUsers}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">
              Average First Attempt Score
            </h3>
            <h2 className="newGradingCaseDetailValue">
              {typeof grade.AverageFirstAttempt === "number"
                ? grade.AverageFirstAttempt.toFixed(1)
                : "\u2014"}
              %
            </h2>
          </div>

          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUserDoctorMessage}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">Case Diagnosis</h3>
            <h2 className="newGradingCaseDetailValue">{caseRow.DIAGNOSIS}</h2>
          </div>
        </div>

        {/* Per-question breakdown — CDM grades one question at a time, unlike
            CCS's flat, category-grouped order list. */}
        {grade.pgolist.pgolist.map((pgoValue, pgoIndex) => {
          const question = grade.qo.find(function (q) {
            return q.QUESTIONID === pgoValue.QuestionId;
          });
          const previousInput =
            (pgoValue.PreviousInput && pgoValue.PreviousInput[0]) || {};

          return (
            <div
              className="newGradingFeedbackItemContainer"
              data-highlight-container="true"
              data-highlight-key={"question-" + pgoIndex}
              data-highlight-type="feedbackItem"
              key={"question-" + pgoIndex}
            >
              <h2 className="newGradingFeedbackItemTitle">
                Question {pgoIndex + 1} (
                {pgoValue.grade ? Math.round(pgoValue.grade * 100) / 100 : 0}
                %) - {question ? question.QUESTION : ""}
              </h2>
              <div className="newGradingFeedbackItemContentWrapper">
                <div
                  className="newGradingFeedbackEvent userEntry"
                  style={Object.assign(
                    {
                      filter:
                        "brightness(" +
                        (this.props.themeNumber !== 0 ? 85 : 100) +
                        "%)",
                    },
                    this.getReadingStyle("neutral"),
                  )}
                >
                  <div className="newGradingFeedbackEventHeader">
                    Your answer
                  </div>
                  <ul>
                    {previousInput.CustomerAnswersBlank
                      ? previousInput.CustomerAnswersBlank.map(
                          function (inputValue, inputIndex) {
                            return (
                              <li key={"answer-" + pgoIndex + "-" + inputIndex}>
                                {inputValue === "" ? (
                                  <b>Option left blank</b>
                                ) : (
                                  inputValue
                                )}
                              </li>
                            );
                          },
                        )
                      : (previousInput.CustomerAnswerMC || []).map(
                          (inputValue, inputIndex) => {
                            return (
                              <li key={"answer-" + pgoIndex + "-" + inputIndex}>
                                {this.getMultipleChoiceOption(inputValue)}
                              </li>
                            );
                          },
                        )}
                  </ul>
                </div>
                {(pgoValue.GradeOut || []).map((gradedValue, gradedIndex) => {
                  const verdict =
                    gradedValue.GradeColor === "Correct - Green"
                      ? "correct"
                      : gradedValue.GradeColor === "Neutral - Blue"
                        ? "neutral"
                        : "incorrect";
                  const verdictClass =
                    gradedValue.GradeColor === "Correct - Green"
                      ? "newGradingFeedbackAnswerCorrect"
                      : gradedValue.GradeColor === "Neutral - Blue"
                        ? "newGradingFeedbackAnswerNeutral"
                        : "newGradingFeedbackAnswerIncorrect";

                  return (
                    <div
                      key={"grade-" + pgoIndex + "-" + gradedIndex}
                      className={
                        "newGradingFeedbackAnswerWrapper " + verdictClass
                      }
                      style={Object.assign(
                        {
                          filter:
                            "brightness(" +
                            (this.props.themeNumber !== 0 ? 85 : 100) +
                            "%)",
                        },
                        this.getReadingStyle(verdict),
                      )}
                    >
                      <h4
                        className="newGradingFeedbackAnswerTitle"
                        style={{ marginBottom: "15px" }}
                      >
                        {gradedValue.EventName}
                      </h4>
                      {gradedValue.OrderName && (
                        <ul className="newGradingFeedbackAnswerItemList">
                          {gradedValue.OrderName.split("\\n").map(
                            function (line, lineIndex) {
                              return (
                                <li
                                  key={
                                    "line-" +
                                    pgoIndex +
                                    "-" +
                                    gradedIndex +
                                    "-" +
                                    lineIndex
                                  }
                                  className="newGradingFeedbackAnswerItem"
                                >
                                  {line}
                                </li>
                              );
                            },
                          )}
                        </ul>
                      )}
                      {gradedValue.Explanation && (
                        <p className="newGradingFeedbackReason">
                          <strong>Reason: </strong>
                          {gradedValue.Explanation}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Case summary */}
        {grade.CaseSummary && (
          <div
            className="newGradingCaseSummaryContainer"
            style={this.getReadingStyle("neutral")}
            data-highlight-container="true"
            data-highlight-key="caseSummary"
            data-highlight-type="caseSummary"
          >
            <h2
              className="newGradingCaseSummaryTitle"
              style={{ color: "inherit" }}
            >
              Case Summary
            </h2>
            <p
              className="newGradingCaseSummaryText"
              style={{ color: "inherit" }}
            >
              {grade.CaseSummary}
            </p>
          </div>
        )}

        <AnimatePresence>
          {this.state.showFeedbackModal && (
            <motion.div
              className="feedbackModalOverlay"
              initial={this.props.animationsDisabled ? false : { opacity: 0 }}
              animate={this.props.animationsDisabled ? false : { opacity: 1 }}
              exit={this.props.animationsDisabled ? false : { opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={this.closeFeedbackModal}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <motion.div
                className="feedbackModalContent"
                initial={
                  this.props.animationsDisabled
                    ? false
                    : { scale: 0.9, opacity: 0 }
                }
                animate={
                  this.props.animationsDisabled
                    ? false
                    : { scale: 1, opacity: 1 }
                }
                exit={
                  this.props.animationsDisabled
                    ? false
                    : { scale: 0.9, opacity: 0 }
                }
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor:
                    this.props.selectedTheme?.secondaryBackgroundColor ||
                    "#fff",
                  borderRadius: "12px",
                  padding: "24px",
                  width: "90%",
                  maxWidth: "500px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                }}
              >
                <h2
                  style={{
                    margin: "0 0 16px 0",
                    color: this.props.selectedTheme?.primaryTextColor || "#333",
                  }}
                >
                  Send Feedback
                </h2>
                {this.state.feedbackSubmitted ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{
                        fontSize: "48px",
                        color: "#4CAF50",
                        marginBottom: "16px",
                      }}
                    />
                    <p
                      style={{
                        color:
                          this.props.selectedTheme?.primaryTextColor || "#333",
                        fontSize: "16px",
                      }}
                    >
                      Thank you for your feedback!
                    </p>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={this.state.feedbackText}
                      onChange={(e) =>
                        this.setState({ feedbackText: e.target.value })
                      }
                      placeholder="Share your thoughts, suggestions, or report issues..."
                      style={{
                        width: "100%",
                        minHeight: "120px",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                        resize: "vertical",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                      }}
                    />
                    {this.state.feedbackError && (
                      <p
                        style={{
                          color: "#f44336",
                          fontSize: "14px",
                          margin: "8px 0 0 0",
                        }}
                      >
                        {this.state.feedbackError}
                      </p>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                        marginTop: "16px",
                      }}
                    >
                      <button
                        type="button"
                        className="tertiaryButton"
                        onClick={this.closeFeedbackModal}
                        style={{ padding: "10px 20px" }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="tertiaryButton"
                        onClick={this.submitFeedback}
                        disabled={
                          this.state.feedbackLoading ||
                          !this.state.feedbackText.trim()
                        }
                        style={{
                          padding: "10px 20px",
                          opacity:
                            this.state.feedbackLoading ||
                            !this.state.feedbackText.trim()
                              ? 0.6
                              : 1,
                        }}
                      >
                        {this.state.feedbackLoading ? (
                          <MDSpinner size={16} singleColor={"#fff"} />
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
}

export default MultiCdmGradeView;
